import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  REFERRAL_EXAMPLE_MAX_BYTES,
  type ReferralExample,
  referralExampleSchema,
} from "./referral-template";

// Note templates from previous notes (clinical lead, 29/10/2026): the
// referral letter "from an example letter" flow for note templates. One to
// three of the clinician's notes (pasted text, PDF, photo; Word files are read
// as text in the browser) are read once and never stored; the API returns a
// draft for the template editor with patient details removed. Nothing is saved
// until the clinician reviews it and saves the template (createTemplate).
export const TEMPLATE_EXAMPLES_MAX = 3;
// Files together: the same request size as one example referral letter.
export const TEMPLATE_EXAMPLES_MAX_BYTES = REFERRAL_EXAMPLE_MAX_BYTES;
export type TemplateExample = ReferralExample;

const decodedBytes = (data: string) =>
  Math.floor((data.length * 3) / 4) - (data.match(/=+$/)?.[0].length ?? 0);
/** Decoded size of the uploaded files (pasted text not counted). */
export const templateExamplesBytes = (
  examples: readonly TemplateExample[],
): number =>
  examples.reduce(
    (total, example) =>
      total + (example.kind === "file" ? decodedBytes(example.data) : 0),
    0,
  );

export const templateExtractionInputSchema = z.strictObject({
  examples: z
    .array(referralExampleSchema)
    .min(1)
    .max(TEMPLATE_EXAMPLES_MAX)
    .refine(
      (examples) =>
        templateExamplesBytes(examples) <= TEMPLATE_EXAMPLES_MAX_BYTES,
      "Files are too large together",
    ),
});
export type TemplateExtractionInput = z.output<
  typeof templateExtractionInputSchema
>;

export const templateDraftSectionSchema = z.object({
  heading: z.string().min(1).max(100),
  instructions: z.string().max(600),
  optional: z.boolean(),
});
export const templateExtractionDraftSchema = z.object({
  name: z.string().min(1).max(100),
  // General instructions for the whole note.
  instructions: z.string().max(4000),
  sections: z.array(templateDraftSectionSchema).max(20),
  style: z.enum(["bullets", "paragraphs"]).nullable(),
  length: z.enum(["concise", "detailed"]).nullable(),
});
export type TemplateExtractionDraft = z.output<
  typeof templateExtractionDraftSchema
>;
export const templateExtractionSchema = z.object({
  draft: templateExtractionDraftSchema,
  // Patient identifiers were found and removed.
  identifiersRemoved: z.boolean(),
  summary: z.string(),
});
export type TemplateExtraction = z.output<typeof templateExtractionSchema>;

export const templateExtractionContract = {
  createTemplateExtraction: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Template extraction unavailable" },
    })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: templateExtractionInputSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/template-extractions",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(templateExtractionSchema),
};
